'use strict';

/**
 * Main entry point for the New User app, which gives new users their password
 * for the first time
 */

// Polyfills for modern JS features
import 'core-js/actual/map';
import 'core-js/actual/set';
import 'core-js/actual/object';
import 'core-js/actual/string';

// Polyfill required for Element.matches in IE and Edge <= 14
if (!Element.prototype.matches) {
    Element.prototype.matches =
        Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
}

import React from 'react';
import ReactDOM from 'react-dom';
import createSagaMiddleware from 'redux-saga';
import createLogger from 'redux-logger';
import { createStore, applyMiddleware } from 'redux';
import { Map } from 'immutable';
import { Provider } from 'react-redux';
import log from 'loglevel';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

import { initApp, initAnalytics } from 'phoenix2/state/init';
import { rootReducer } from 'phoenix2/state/new-user/reducers';
import { rootSaga } from 'phoenix2/state/new-user/sagas';
import { initKeyboardShortcutHandler } from 'phoenix2/components/keyboard';
import { ErrorReportingBoundary } from '../components/error-boundaries';
import { reportError } from 'phoenix2/state/errors/actions';
import { CLIENT_ERROR_LEVEL_WARNING } from 'phoenix2/state/errors/errors';
import { SENTRY_SCHOOL_DISPLAY_NAME, SENTRY_SCHOOL_NAME } from 'phoenix2/utils/sentry-utils';

// import all css for webpack
import '../../less/main.less';

// Import font awesome icons
import { library, dom } from '@fortawesome/fontawesome-svg-core';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons/faCaretDown';
import { faAngleRight } from '@fortawesome/free-solid-svg-icons/faAngleRight';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons/faExclamationTriangle';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons/faCheckCircle';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons/faTimesCircle';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons/faCircleNotch';

library.add(faCaretDown);
library.add(faAngleRight);
library.add(faExclamationTriangle);
library.add(faCheckCircle);
library.add(faTimesCircle);
library.add(faCircleNotch);
dom.watch();

// import katex
import 'katex/dist/katex.min.css';

// just copy over everything in copied-assets and keep the same directory structure (the directory
// structure is determined by the loading config in the webpack.config files)
const copiedContext = require.context('../../copied-assets', true);
copiedContext.keys().forEach(copiedContext);

const APPLICATION_NAME = 'newuser';

const sagaMiddleware = createSagaMiddleware();
const middleware = [sagaMiddleware];

if (process.env.REDUX_LOGGER === 'on') {
    const reduxLogger = createLogger();
    middleware.push(reduxLogger);
}
if (process.env.DEFAULT_LOG_LEVEL) {
    log.setDefaultLevel(process.env.DEFAULT_LOG_LEVEL);
}

const store = createStore(rootReducer, Map(), applyMiddleware(...middleware));

const analyticsOnError = (error, frames = '', level = CLIENT_ERROR_LEVEL_WARNING) => {
    store.dispatch(reportError(error, frames, level));
};

const analytics = initAnalytics(
    settings.analyticsConfig,
    settings.schoolID,
    APPLICATION_NAME,
    analyticsOnError,
);
analytics.flushMaybe();

let runningSaga = sagaMiddleware.run(rootSaga, null, analytics);

// Initialise the app, but we do not need Katex or Drag and Drop
initApp(store, { useKatex: false, useDnd: false });

let environment = settings.environment;
if (environment === 'spx001') {
    environment = 'production';
}
Sentry.init({
    dsn: 'https://394ecdfb2ad7400289e765030a59efc9@o1086305.ingest.sentry.io/4504259277684736',
    integrations: [new BrowserTracing()],
    environment,
    // don't use Sentry for dev or unknown (likely dev envs without the setting) environments
    enabled: environment !== 'development' && environment !== 'unknown',
    // limit rate of transactions to 1% in production
    tracesSampleRate: environment !== 'production' ? 1 : 0.01,
    // Exclude 'ResizeObserver' errors as these are safe to ignore
    // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded/50387233#50387233
    ignoreErrors: ['ResizeObserver'],
});
Sentry.setTag(SENTRY_SCHOOL_NAME, `schools/${settings.schoolID}`);
Sentry.setTag(SENTRY_SCHOOL_DISPLAY_NAME, settings.schoolName);

// Initialise the handler for keyboard shortcuts - use the document as the
// root element, and use the default keyboard handler, as we only need one
initKeyboardShortcutHandler(store, document);
const rootElement = document.getElementById('app-container');
let render = () => {
    const App = require('phoenix2/components/apps/new-user/new-user-container.jsx').default;
    ReactDOM.render(
        <Sentry.ErrorBoundary>
            <Provider store={store}>
                <ErrorReportingBoundary>
                    <App />
                </ErrorReportingBoundary>
            </Provider>
        </Sentry.ErrorBoundary>,
        rootElement,
    );
};

// eslint-disable-next-line
console.log('Check the changes');

if (process.env.NODE_ENV === 'development') {
    if (module.hot) {
        const renderApp = render;
        const renderError = error => {
            const RedBox = require('redbox-react').default;
            ReactDOM.render(<RedBox error={error} />, rootElement);
        };

        render = () => {
            try {
                renderApp();
            } catch (error) {
                renderError(error);
            }
        };

        // Accept updates for the components
        module.hot.accept('phoenix2/components/apps/new-user/new-user-container.jsx', () => {
            setTimeout(render);
        });

        // Accept updates for the redux store
        module.hot.accept('phoenix2/state/new-user/reducers', () => {
            const newRootReducer = require('phoenix2/state/new-user/reducers').rootReducer;
            store.replaceReducer(newRootReducer);
        });

        // Accept updates for the sagas
        module.hot.accept('phoenix2/state/new-user/sagas', () => {
            const newRootSaga = require('phoenix2/state/new-user/sagas').rootSaga;
            runningSaga.cancel();
            runningSaga.done.then(() => {
                runningSaga = sagaMiddleware.run(function* replacedSaga(action) {
                    yield newRootSaga();
                });
            });
        });
    }
}

render();
